import { useCallback, type FunctionComponent } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import { useIsMemberOfOrganization } from '@trello/business-logic-react/organization';
import {
  PremiumFeatures,
  useWorkspaceHasPremiumFeature,
} from '@trello/entitlements';
import { useFeatureGate } from '@trello/feature-gate-client';
import { useBoardId, useWorkspaceId } from '@trello/id-context';

import { BoardTag } from 'app/src/components/BoardTag';
import { BoardTileLarge } from 'app/src/components/BoardTile/BoardTileLarge';
import { NewBoardTileLarge } from 'app/src/components/BoardTile/NewBoardTileLarge';
import { EditableCollectionsSelect } from 'app/src/components/CollectionsSelect/EditableCollectionsSelect';
import { useWorkspaceBoardsFilterState } from './useWorkspaceBoardsFilterState';
import { useWorkspaceBoardCollectionsFragment } from './WorkspaceBoardCollectionsFragment.generated';
import { useWorkspaceCollectionsFragment } from './WorkspaceCollectionsFragment.generated';

import styles from './WorkspaceBoardsListItem.less';

export const WorkspaceBoardsListItem: FunctionComponent = () => {
  const memberId = useMemberId();
  const boardId = useBoardId()!;
  const workspaceId = useWorkspaceId()!;

  const { data: organization } = useWorkspaceCollectionsFragment({
    from: { id: workspaceId },
  });
  const { data: board } = useWorkspaceBoardCollectionsFragment({
    from: { id: boardId },
  });

  const { tagFilter, setTagFilter } = useWorkspaceBoardsFilterState();
  const onClickTag = useCallback(
    (tagId: string) => () => {
      Analytics.sendClickedButtonEvent({
        buttonName: 'collectionButton',
        source: 'workspaceBoardsScreen',
      });
      setTagFilter(tagId);
    },
    [setTagFilter],
  );

  const onBoardClick = useCallback(() => {
    return Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'tile',
      actionSubjectId: 'boardTile',
      source: 'workspaceBoardsScreen',
      containers: {
        board: {
          id: boardId,
        },
      },
    });
  }, [boardId]);

  const isTaggingEnabled = useWorkspaceHasPremiumFeature(
    workspaceId,
    PremiumFeatures.tags,
  );
  const canAddTag = useIsMemberOfOrganization({
    idMember: memberId,
    idOrganization: workspaceId,
  });
  const tags = (organization?.tags ?? []).filter((tag) =>
    board?.idTags?.includes(tag.id),
  );

  const { value: isRedesignEnabled } = useFeatureGate(
    'trello_personal_productivity_release',
  );

  const boardTagsComponent = isTaggingEnabled ? (
    <div className={styles.boardTagsContainer}>
      <div className={styles.boardTags}>
        {tags.map((tag) => (
          <BoardTag
            className={styles.tag}
            text={tag.name}
            key={tag.id}
            selected={tag.id === tagFilter}
            onClick={onClickTag(tag.id)}
          />
        ))}
      </div>
      {canAddTag && (
        <div className={styles.addTag}>
          <EditableCollectionsSelect />
        </div>
      )}
    </div>
  ) : null;

  if (isRedesignEnabled) {
    return (
      <>
        <NewBoardTileLarge
          boardId={boardId}
          onBoardClick={onBoardClick}
          className={styles.newBoardTile}
        />
        {boardTagsComponent}
      </>
    );
  }

  return (
    <div className={styles.container}>
      <BoardTileLarge boardId={boardId} onBoardClick={onBoardClick} />
      {boardTagsComponent}
    </div>
  );
};
